import {
  Capacidad,
  Reparto,
  Operacion,
  EnumBloqueoCapacidad,
  EnumTipoOperacion,
  EnumOperationState,
  EnumDispatchType
} from '../../api';
import {
  AllocationModel,
  OperationAllocationsViewModel,
  AllocationFilterModel
} from './allocations.model';
import { enumValueToKey, enumKeyToValue } from '../../helpers';
import {
  getAllocationActionsConfiguration,
  getAllocationSecondaryActionsConfiguration,
  canSend
} from './allocation.service';
import { DispatchRequest, OperationRequest, AllocationRequest, OperationDispatch } from '../../api/oData/requests';
import i18n from '../../i18n';
import { DecimalScale, UserPermission } from '@globaleris/ui.library';
import { formatLocaleNumber, formatLocalePercentage } from '../../helpers/numberHelper';
import { hasPermission } from '../../helpers/permissionsHelper';
import { Resources } from '../../model/RBAC/constants';
import { getActiveBalanceZone } from '../../redux';

export const capacidadArrayFromApi = (items: Capacidad[]): AllocationFilterModel[] => {
  return items && items.map(capacidadFromApi);
};

export const capacidadFromApi = (item: Capacidad): AllocationFilterModel => {
  return (
    item && {
      description: `${i18n.t(`enums:EnumServicio.${item.tipoContrato}`)} - ${item.elementoLogistico?.codElementoLogistico
        }`,
      id: item.id
    }
  );
};

export const repartoArrayFromApi = (items: Reparto[]): AllocationModel[] => {
  return items && items.map(repartoFromApi);
};

export const repartoFromApi = (item: Reparto): AllocationModel => {
  return (
    item && {
      id: item.id,
      quantity: item.cantidad,
      capacity: item.productoCapacidad?.qdContratada,
      productQdHiredFormatted: formatLocaleNumber(
        item.productoCapacidad?.qdContratada,
        DecimalScale.ENERGY
      ),
      sendQuantity: item.cantidadEnvio,
      TSOQuantity: item.cantidadTSO,
      productCapacityId: item.productoCapacidad?.id,
      productCapacityCode: item.productoCapacidad?.codProductoCapacidad,
      productCapacityType: item.productoCapacidad?.tipoProductoCapacidad,
      productCapacityNatureType: item.productoCapacidad?.tipoNaturaleza,
      productQdHired: item.productoCapacidad?.qdContratada,
      bundledParentProduct: item.productoCapacidad?.productoPadre,
      locked: enumValueToKey(EnumBloqueoCapacidad, item.productoCapacidad?.bloqueo),
      usePercentage: item.cantidad! / item.productoCapacidad?.qdContratada!,
      allocatedQuantityBreakdown: `${formatLocaleNumber(
        item.cantidad,
        DecimalScale.ENERGY
      )} (${formatLocalePercentage(item.cantidad! / item.productoCapacidad?.qdContratada!)}%)`,
      productCapacityBreakdown: `${item.productoCapacidad?.codProductoCapacidad} (${item.productoCapacidad?.tipoProductoCapacidad} - ${item.productoCapacidad?.tipoNaturaleza})`,
      TSOInformationBreakdown: `${formatLocaleNumber(
        item.cantidadTSO!,
        DecimalScale.ENERGY
      )} (${formatLocalePercentage(item.cantidadTSO! / item.productoCapacidad?.qdContratada!)}%)`,
      lastSendInformationBreakdown: `${formatLocaleNumber(
        item.cantidadEnvio!,
        DecimalScale.ENERGY
      )} (${formatLocalePercentage(item.cantidadEnvio! / item.productoCapacidad?.qdContratada!)}%)`,
      isModified: false,
      actionsConfiguration: getAllocationActionsConfiguration(
        hasPermission(Resources.Nomination, [UserPermission.EDIT])
      ),
      secondaryActionsConfiguration: getAllocationSecondaryActionsConfiguration()
    }
  );
};

export const associateIcon = (): any => { };

export const operacionArrayFromApi = (items: Operacion[]): OperationAllocationsViewModel[] => {
  return items && items.map(operacionFromApi);
};

export const operacionFromApi = (item: Operacion): OperationAllocationsViewModel => {
  return (
    item && {
      id: item.id,
      type: enumValueToKey(EnumTipoOperacion, item.tipoOperacion),
      state: enumKeyToValue(EnumOperationState, item.estadoOperacion),
      stateDescription: item.estadoOperacion
        ? i18n.t(`enums:EnumOperationState.${item.estadoOperacion}`)
        : '',
      operationDate: item.fechaOperacion,
      quantity: item.cantidad,
      sendQuantity: item.cantidadEnvio,
      TSOQuantity: item.cantidadTSO,
      allocations: repartoArrayFromApi(item.repartos),
      allocatedQuantityBreakdownSubRows: [],
      productCapacityBreakdownSubRows: [],
      lastSendBreakdownSubRows: [],
      TSOInformationBreakdownSubRows: [],
      capacitySubRows: [],
      canSend: canSend(enumKeyToValue(EnumOperationState, item.estadoOperacion))
    }
  );
};

export const operacionToApiRequestPatch = (
  item: OperationAllocationsViewModel
): OperationRequest => {
  return (
    item && {
      id: item.id,
      tipoOperacion: enumKeyToValue(EnumTipoOperacion, item.type),
      estadoOperacion: enumKeyToValue(EnumOperationState, item.state),
      cantidad: item.quantity
    }
  );
};

export const allocationToApiRequestPatch = (item: AllocationModel): AllocationRequest => {
  return (
    item && {
      id: item.id,
      cantidad: item.quantity
    }
  );
};

export const dispatchOperationToApiRequestPost = (
  dispatchType: EnumDispatchType,
  item: OperationAllocationsViewModel,
  idCompany: number
): DispatchRequest => {
  return (
    item && {
      dispatchType: enumKeyToValue(EnumDispatchType, dispatchType),
      companyID: idCompany,
      operations: [
        {
          id: item.id,
          operationType: enumKeyToValue(EnumTipoOperacion, item.type),
          balanceZone: getActiveBalanceZone()?.code!
        }
      ]
    }
  );
};

export const dispatchOperationsToApiRequestPost = (
  dispatchType: EnumDispatchType,
  items: OperationAllocationsViewModel[],
  idCompany: number
): DispatchRequest => {
  return (
    items && {
      dispatchType: enumKeyToValue(EnumDispatchType, dispatchType),
      companyID: idCompany,
      operations: items.map(
        (item): OperationDispatch => {
          return { id: item.id, operationType: enumKeyToValue(EnumTipoOperacion, item.type), balanceZone: getActiveBalanceZone()?.code! };
        }
      )
    }
  );
};
